/**
 * Assignment Utilities
 *
 * Utilities for resolving assignment IDs between Shifts and ShiftUnits.
 * This module handles the migration from Shift IDs to ShiftUnit IDs in assignments.
 *
 * KEY DESIGN PRINCIPLE:
 * Both Shift IDs and ShiftUnit IDs are UUIDs - they are INDISTINGUISHABLE by format.
 * All detection is done via LOOKUP, not format-based detection.
 */

import { toLocalDateKey } from '@zag/calendar/models';
import type { Logger } from '../types/logger';
import type { ShiftUnitInfo } from '../models/shift-unit.model';
import type { PlanInstanceOverrides, ShiftUnitOverride } from '../models/plan-instance.model';

// Re-export for convenience
export type { Logger } from '../types/logger';
export type { ShiftUnitInfo } from '../models/shift-unit.model';

/**
 * Lookup structures for assignment resolution.
 * Since IDs are UUIDs (indistinguishable by format), we use lookups.
 */
export interface AssignmentResolver {
  /** Set of known ShiftUnit IDs (for O(1) lookup) */
  shiftUnitIds: Set<string>;
  /** Map of Shift ID → ShiftUnit infos (for legacy resolution) */
  shiftToUnitsMap: Map<string, ShiftUnitInfo[]>;
}

/**
 * Result of assignment ID resolution
 */
export interface ResolutionResult {
  /**
   * Type of the ID:
   * - 'shift_unit': ID is a known ShiftUnit ID
   * - 'shift': ID is a known Shift ID (legacy format)
   * - 'unknown': ID not found in any lookup
   */
  type: 'shift_unit' | 'shift' | 'unknown';
  /**
   * Resolved ID:
   * - For 'shift_unit': The same ID (already correct)
   * - For 'shift': Preliminary ShiftUnit ID (first unit). MUST call resolveLegacyShiftId() for deterministic resolution.
   * - For 'unknown': The original ID (unchanged)
   */
  resolvedId: string;
}

/**
 * Create an AssignmentResolver from a list of ShiftUnit infos.
 *
 * @param shiftUnits - Array of ShiftUnit info objects
 * @returns AssignmentResolver with populated lookup structures
 */
export function createAssignmentResolver(shiftUnits: ShiftUnitInfo[]): AssignmentResolver {
  const shiftUnitIds = new Set<string>();
  const shiftToUnitsMap = new Map<string, ShiftUnitInfo[]>();

  for (const unit of shiftUnits) {
    shiftUnitIds.add(unit.id);

    const existing = shiftToUnitsMap.get(unit.shiftId);
    if (existing) {
      existing.push(unit);
    } else {
      shiftToUnitsMap.set(unit.shiftId, [unit]);
    }
  }

  return { shiftUnitIds, shiftToUnitsMap };
}

/**
 * Resolve an assignment ID to determine if it's a Shift ID or ShiftUnit ID.
 *
 * IMPORTANT: IDs are UUIDs - they cannot be distinguished by format.
 * This function uses LOOKUP-based detection.
 *
 * USAGE PATTERN:
 * ```typescript
 * const result = resolveAssignmentId(id, resolver);
 * let finalShiftUnitId: string;
 *
 * if (result.type === 'shift') {
 *   // Must refine with date context for deterministic resolution
 *   finalShiftUnitId = resolveLegacyShiftId(id, assignmentDate, resolver.shiftToUnitsMap, logger);
 * } else {
 *   finalShiftUnitId = result.resolvedId;
 * }
 * ```
 *
 * @param id - The ID to resolve
 * @param resolver - AssignmentResolver with lookup structures
 * @returns ResolutionResult with type and resolved ID
 */
export function resolveAssignmentId(id: string, resolver: AssignmentResolver): ResolutionResult {
  // Check if it's a known ShiftUnit ID
  if (resolver.shiftUnitIds.has(id)) {
    return { type: 'shift_unit', resolvedId: id };
  }

  // Check if it's a known Shift ID
  const units = resolver.shiftToUnitsMap.get(id);
  if (units && units.length > 0) {
    // WARNING: resolvedId is preliminary (first unit).
    // Callers MUST call resolveLegacyShiftId() with the assignment date
    // to get the correct deterministic resolution for multi-unit shifts.
    return { type: 'shift', resolvedId: units[0].id };
  }

  // ID not found in any lookup
  return { type: 'unknown', resolvedId: id };
}

/**
 * Resolve a legacy Shift ID to a ShiftUnit ID.
 *
 * Resolution rules (deterministic):
 * 1. Shift has exactly 1 ShiftUnit → Use that ShiftUnit
 * 2. Shift has multiple ShiftUnits → Use the earliest startTime on the same date
 * 3. Shift has multiple ShiftUnits, none match date → Log warning, use earliest overall
 * 4. Shift has 0 ShiftUnits → Throw error (data integrity violation)
 * 5. Shift not found → Throw error (data integrity violation)
 *
 * @param shiftId - The legacy Shift ID to resolve
 * @param assignmentDate - The assignment date (YYYY-MM-DD) for context
 * @param shiftToUnitsMap - Map of Shift ID → ShiftUnit infos
 * @param logger - Logger for warnings
 * @returns Resolved ShiftUnit ID
 * @throws Error if shift has no ShiftUnits or is not found
 */
export function resolveLegacyShiftId(
  shiftId: string,
  assignmentDate: string,
  shiftToUnitsMap: Map<string, ShiftUnitInfo[]>,
  logger: Logger
): string {
  const units = shiftToUnitsMap.get(shiftId);

  // Case 4 & 5: No units found
  if (!units || units.length === 0) {
    throw new Error(`Shift ${shiftId} has no ShiftUnits - data integrity violation`);
  }

  // Case 1: Single unit - unambiguous
  if (units.length === 1) {
    return units[0].id;
  }

  // Case 2: Multiple units - find one matching the assignment date
  const sameDateUnits = units.filter(u => {
    const unitDate = toLocalDateKey(new Date(u.startTime));
    return unitDate === assignmentDate;
  });

  if (sameDateUnits.length > 0) {
    // Sort by startTime, return earliest
    sameDateUnits.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    return sameDateUnits[0].id;
  }

  // Case 3: No same-date match - log warning and use earliest overall
  logger.warn(
    `Legacy Shift ID ${shiftId} has ${units.length} units but none match date ${assignmentDate}. ` + `Resolving to earliest unit.`
  );

  // Sort all units by startTime and return earliest
  const sortedUnits = [...units].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  return sortedUnits[0].id;
}

/**
 * Look up the per-employee time override for `(dateKey, shiftUnitId, employeeId)`.
 *
 * Reads from two parallel storage locations and merges them — UI-written
 * `employeeOverrides[employeeId].{startTime,endTime}` wins over legacy
 * `assignmentTimes[employeeId].{startTime,endTime}` on a per-field basis.
 * Returns `null` when no override exists in either storage path.
 */
export function getAssignmentTimeOverride(
  overrides: PlanInstanceOverrides | null | undefined,
  dateKey: string,
  shiftUnitId: string,
  employeeId: string
): { startTime?: string; endTime?: string; breakMinutes?: number } | null {
  const dateOverride = overrides?.[dateKey];
  if (!dateOverride) return null;
  const unitOverride: ShiftUnitOverride | undefined = dateOverride[shiftUnitId];
  const empEntry = (
    unitOverride as { employeeOverrides?: Record<string, { startTime?: string; endTime?: string; breakMinutes?: number }> } | undefined
  )?.employeeOverrides?.[employeeId];
  const timesEntry = (unitOverride?.['assignmentTimes'] as Record<string, { startTime?: string; endTime?: string }> | undefined)?.[
    employeeId
  ];
  const result: { startTime?: string; endTime?: string; breakMinutes?: number } = {};
  const startTime = empEntry?.startTime ?? timesEntry?.startTime;
  const endTime = empEntry?.endTime ?? timesEntry?.endTime;
  if (typeof startTime === 'string' && startTime.length > 0) result.startTime = startTime;
  if (typeof endTime === 'string' && endTime.length > 0) result.endTime = endTime;
  if (typeof empEntry?.breakMinutes === 'number') result.breakMinutes = empEntry.breakMinutes;
  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Compute the effective `{ startTime, endTime }` for a single shift assignment
 * given the override chain. Resolution order (each property independently):
 *
 *   1. `overrides[dateKey][shiftUnitId].employeeOverrides[employeeId]` (UI-written, per-employee)
 *   2. `overrides[dateKey][shiftUnitId].assignmentTimes[employeeId]` (legacy / seed-written, per-employee)
 *   3. `overrides[dateKey][shiftUnitId].startTime` / `endTime` (unit-level)
 *   4. `unitStartTime` / `unitEndTime` (template default)
 */
export function getEffectiveAssignmentTimes(input: {
  overrides: PlanInstanceOverrides | null | undefined;
  dateKey: string;
  shiftUnitId: string;
  employeeId: string;
  unitStartTime: string;
  unitEndTime: string;
  unitBreakMinutes?: number | null;
}): { startTime: string; endTime: string; breakMinutes?: number } {
  const unitOverride = input.overrides?.[input.dateKey]?.[input.shiftUnitId] as ShiftUnitOverride | undefined;
  const perEmployee = getAssignmentTimeOverride(input.overrides, input.dateKey, input.shiftUnitId, input.employeeId);
  return {
    startTime: perEmployee?.startTime ?? unitOverride?.startTime ?? input.unitStartTime,
    endTime: perEmployee?.endTime ?? unitOverride?.endTime ?? input.unitEndTime,
    breakMinutes: perEmployee?.breakMinutes ?? input.unitBreakMinutes ?? undefined,
  };
}
